import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { useIntl } from "react-intl";

//Components
import { Grid, Typography, Container, Paper, Divider } from "@mui/material";
import { Button } from "@gpstrackit/react-controls"


//EndPoints
import { endpoints } from 'Core/defaultValues';

//Views
import DataView from 'Components/DataView';
import CreateEditDealer from '../createEditDealer';

//Security
import { HasPermission } from "Core/security";

//Notification
import { NotificationManager } from "Components/Notifications";

//Actions
import {
    dealersCreateEditDealerOpenModal,
    clearRedux
} from 'Redux/actions';

const DealersTab = (props) => {
    const { brandId, height, brand } = props
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const createEditDealerOModal = useSelector(state => state.createEditDealerRedux.openModal);
    const user = useSelector(state => state.securityRedux.user);

    const [columns, setColumns] = useState([

        {
            key: "id",
            title: messages['dealers_tab_id'],
            width: 25,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return row?.id',
            code: "dealers_tab_id",
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "name",
            field: "name",
            title: messages['dealers_tab_name'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "dealers_tab_name",
            render: (item) => {
                let name = item?.name
                if (name?.length >= 7) {
                    name = name?.substring(0, 40) + "...";
                }
                return (
                    <NavLink to={`/app/dealers/overview/${item?.id}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{name}</span>
                    </NavLink>
                )
            },
            export: true,
            functionString: 'return row?.name'
        },
        {
            key: "email",
            field: "email",
            title: messages['dealers_tab_email'],
            width: 350,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "dealers_tab_email",
            render: (item) => {
                return <span>{item?.email}</span>
            },
            export: true,
            functionString: 'return row?.email'
        },
        {
            key: "username",
            field: "username",
            title: messages['dealers_tab_username'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "dealers_tab_username",
            render: (item) => {
                return <span>{item?.username}</span>
            },
            export: true,
            functionString: 'return row?.username'
        },
        {
            key: "masterCompanyName",
            title: messages['dealers_tab_master_company_name'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            code: "dealers_tab_master_company_name",
            render: (item) => {
                let name = item?.masterCompany?.name || ""
                if (name?.length >= 7) {
                    name = name?.substring(0, 40) + "...";
                }
                return (
                    <NavLink to={`/app/accounts/overview/${item?.masterCompanyId}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{name}</span>
                    </NavLink>
                )
            },
            export: true,
            functionString: 'return row?.masterCompany?.name || ""'
        },
        {
            key: "crmId",
            title: messages['dealers_tab_crm'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            code: "dealers_tab_crm",
            render: (item) => {
                return <a
                    style={{ color: 'black' }}
                    href={`${endpoints.SALESFORCE_URL}lightning/r/Account/${item?.masterCompany?.crmId}/view`}
                    target="_blank"
                >
                    {item?.masterCompany?.crmId}
                </a>
            },
            export: true,
            functionString: 'return row?.masterCompany?.crmId'
        },
        {
            key: "brand",
            field: "brand.title",
            title: messages['dealers_tab_brand'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            code: "dealers_tab_brand",
            render: (item) => {
                return <span>{item?.brand?.title}</span>
            },
            export: true,
            functionString: 'return row?.brand?.title'
        },
    ])
    const [searchFields, setSearchFields] = useState(["name", "email", "username", "company_name"]);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        return () => {
            dispatch(clearRedux("COMMON", {
                'dealers-tab': {}
            }))
        }
    }, [])

    return (
        <div style={{ marginTop: 10 }}>
            <DataView
                ref={tableRef}
                exportName="dealers-tab"
                dinamicQuery
                name="pm-dealers-tab"
                entity="dealers-tab"
                graphqlFunction="searchDealers"
                graphqlSort="DealerSortInput"
                graphqlConditions="DealerSearchInput"
                customQuery="id masterCompanyId masterCompany { name crmId }"
                columns={columns}
                noFilters
                asyncSelect
                searchConditions={["name"]}
                searchOptionValue="id"
                searchOptionLabel="name"
                customSearch={{ fields: ["brandId"], terms: [brandId], exact: true, conditionOperator: "AND" }}
                onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                    //current conditions
                    let currentConditions = {}
                    let currentTags = {}

                    if (!conditions && tags) {
                        currentConditions = tableRef?.current?.conditions;
                        currentTags = tags;
                    } else {
                        currentConditions = conditions || [];
                        currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                        if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                    }

                    currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name' && item?.fields[0] !== 'brandId');

                    let tagsById = currentTags.filter((item) => typeof item.id === "number");
                    if (tagsById?.length > 0) {
                        //New Items
                        let currentIds = tagsById.map((item) => (item.id));
                        if (currentIds.length > 0) {
                            currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                        }
                    }

                    let tagsByLabel = currentTags.filter((item) => typeof item.id === "string");

                    if (tagsByLabel.length > 0) {
                        let currentLabels = tagsByLabel.map((item) => (item.name))
                        if (currentLabels.length > 0) {
                            currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                        }
                    }

                    currentConditions.push({ fields: ["brandId"], terms: [brandId], exact: true, conditionOperator: "AND" });

                    if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions);
                    else tableRef?.current?.getData(null, rows, sort, currentConditions, newColumns);
                }}
                topheight={200}
                height={height || 500}
                onChangeTotal={(total) => setTotal(total)}
                onFilters={() => { setOpenPanel(!openPanel) }}
                noSplit
                onChangeColumns={(items) => {
                    let currentColumns = items.map((item) => ({
                        ...item,
                        title: item.code ? messages[item.code] : item.title
                    }))
                    setColumns(currentColumns)
                }}
                tableButtons={["export", 'orderColumn']}
                options={[
                    {
                        id: "dealers_tab_edit",
                        label: messages['dealers_tab_edit'],
                        single: true,
                        show: true,
                        disabled: !HasPermission(user, "dea", "u"),
                        action: (items) => {
                            let currentItem = items.find(item => item.id);
                            dispatch(dealersCreateEditDealerOpenModal(currentItem))
                        }
                    }
                ]}
                menuOptions={() => {
                    return [
                        messages['dealers_tab_edit']
                    ]
                }}
                onClickMenu={(event, item) => {

                    if (event === messages['dealers_tab_edit']) {
                        if (HasPermission(user, "dea", "u")) {
                            dispatch(dealersCreateEditDealerOpenModal(item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['dealers_tab_edit'],
                                5000,
                            );
                        }
                    }

                }}

                extraButton={() => {
                    return (
                        <div style={{ margin: '0 5px' }}>
                            {
                                HasPermission(user, "dea", "c") &&
                                <Button
                                    disabled={!HasPermission(user, "dea", "c")}
                                    className="btn-dark-blue"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        dispatch(dealersCreateEditDealerOpenModal())
                                    }}
                                >
                                    {messages['dealers_tab_add_dealer']}
                                </Button>
                            }
                        </div>
                    )
                }}
            />
            {
                createEditDealerOModal && <CreateEditDealer tableRef={tableRef} brandParam={brand} />
            }
        </div>
    )
}
export default DealersTab;